import { doc, getDoc, onSnapshot, setDoc, serverTimestamp, Timestamp } from 'firebase/firestore'
import type { DocumentSnapshot } from 'firebase/firestore'
import { db } from '../config/firebase'
import type { ConsentApp, UserConsentRecord } from '../types/consent'
import { PRIVACY_VERSION, TOS_VERSION } from '../data/legalDocuments'

const safeDate = (v: unknown): Date => {
  if (!v) return new Date()
  if (v instanceof Date) return v
  if (v instanceof Timestamp) return v.toDate()
  if (typeof (v as { toDate?: unknown }).toDate === 'function') {
    return (v as { toDate: () => Date }).toDate()
  }
  return new Date()
}

const toUserConsentRecord = (
  userId: string,
  snap: DocumentSnapshot
): UserConsentRecord | null => {
  if (!snap.exists()) return null
  const data = snap.data()
  return {
    user_id: userId,
    tos_version: data.tos_version,
    privacy_version: data.privacy_version,
    accepted_at: safeDate(data.accepted_at),
    app: data.app,
    user_agent: data.user_agent,
  }
}

export async function getUserConsent(userId: string): Promise<UserConsentRecord | null> {
  const ref = doc(db, 'user_consents', userId)
  const snap = await getDoc(ref)
  return toUserConsentRecord(userId, snap)
}

/**
 * Live-watch the user's consent record; returns the unsubscribe function.
 * Fires on every change — including an acceptance made in another tab or
 * device — so a stale tab's popup closes itself the moment consent lands.
 * On read errors onChange is NOT called: the consent gate keeps its last
 * known answer rather than mistaking a transient failure for "never
 * accepted" and re-prompting a user who already consented.
 */
export function watchUserConsent(
  userId: string,
  onChange: (record: UserConsentRecord | null) => void
): () => void {
  const ref = doc(db, 'user_consents', userId)
  return onSnapshot(
    ref,
    (snap) => onChange(toUserConsentRecord(userId, snap)),
    (error) => {
      console.warn('[consent] user consent watch failed (keeping last known state):', error)
    }
  )
}

export async function recordUserConsent(
  userId: string,
  app: ConsentApp = 'admin'
): Promise<void> {
  const ref = doc(db, 'user_consents', userId)
  await setDoc(ref, {
    user_id: userId,
    tos_version: TOS_VERSION,
    privacy_version: PRIVACY_VERSION,
    accepted_at: serverTimestamp(),
    app,
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
  })
}

export function isUserConsentCurrent(record: UserConsentRecord | null): boolean {
  if (!record) return false
  return record.tos_version === TOS_VERSION && record.privacy_version === PRIVACY_VERSION
}
